import { useState } from "react";
import {
  VIDEO_DURATIONS,
  VIDEO_FRAME_RATES,
  VIDEO_PLATFORM_SPECS,
  getOpenAIVideoModelProfile,
  type CreateJobInput,
  type OpenAIVideoModel,
  type StylePreset,
  type VideoDuration,
  type VideoFrameRate,
  type VideoPlatform,
} from "@farzin/contracts";
import { PlatformPicker } from "./platform-picker";
import { StylePicker } from "./style-picker";
import { getModelExperience } from "../model-experience";

export interface StoryFormProps {
  readonly disabled: boolean;
  readonly model: OpenAIVideoModel;
  readonly onSubmit: (input: CreateJobInput) => Promise<void>;
}

const EXAMPLE =
  "A young astronomer watches a glowing whale swim between the skyscrapers during a quiet midnight storm.";

export function StoryForm({
  disabled,
  model,
  onSubmit,
}: StoryFormProps): React.JSX.Element {
  const profile = getOpenAIVideoModelProfile(model);
  const experience = getModelExperience(model);
  const [story, setStory] = useState("");
  const [durationSeconds, setDurationSeconds] = useState<VideoDuration>(
    profile.tier === "rapid" ? 4 : 8,
  );
  const [stylePreset, setStylePreset] = useState<StylePreset>("neon-rain");
  const [platform, setPlatform] = useState<VideoPlatform>("tiktok");
  const [frameRate, setFrameRate] = useState<VideoFrameRate>(30);

  const choosePlatform = (nextPlatform: VideoPlatform): void => {
    setPlatform(nextPlatform);
    const minimum = VIDEO_PLATFORM_SPECS[nextPlatform].minimumFrameRate;
    if (frameRate < minimum) setFrameRate(minimum);
  };

  const submit = async (
    event: React.SyntheticEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const normalized = story.trim();
    if (normalized.length < 12) return;
    await onSubmit({
      story: normalized,
      durationSeconds,
      stylePreset,
      platform,
      frameRate,
    });
  };

  return (
    <form
      className="creator-panel pixel-border"
      onSubmit={(event) => void submit(event)}
    >
      <div className="section-heading">
        <span className="step-number">01</span>
        <div>
          <h2>Tell your story</h2>
          <p>{experience.storyHint} Write in English.</p>
        </div>
      </div>

      <label className="story-field">
        <span className="sr-only">Story or scene description</span>
        <textarea
          value={story}
          maxLength={4_000}
          rows={6}
          placeholder={EXAMPLE}
          onChange={(event) => setStory(event.target.value)}
        />
        <span className="character-count">{story.length} / 4000</span>
      </label>
      <button
        className="example-button"
        type="button"
        onClick={() => setStory(EXAMPLE)}
      >
        ✦ Use an example
      </button>

      <div className="section-heading compact">
        <span className="step-number">02</span>
        <div>
          <h2>Choose a mood</h2>
          <p>Every style stays distinctly pixel-crafted.</p>
        </div>
      </div>
      <StylePicker value={stylePreset} onChange={setStylePreset} />
      <div className="cast-lock">
        <span>CAST LOCKED</span>
        <div>
          <strong>Mika + Ren</strong>
          <p>
            Couple scenes reuse the same clearly adult, Japanese pixel-anime
            character designs.
          </p>
        </div>
      </div>

      <div className="section-heading compact">
        <span className="step-number">03</span>
        <div>
          <h2>Choose a platform</h2>
          <p>Size, orientation, and export format are set automatically.</p>
        </div>
      </div>
      <PlatformPicker value={platform} onChange={choosePlatform} />
      <div className="format-summary">
        <span>EXPORT</span>
        <strong>
          {VIDEO_PLATFORM_SPECS[platform].width}×
          {VIDEO_PLATFORM_SPECS[platform].height}
        </strong>
        <span>{VIDEO_PLATFORM_SPECS[platform].aspectRatio}</span>
        <span>MP4 · H.264</span>
        <span className="model-output">{profile.outputDescription}</span>
      </div>

      <div className="section-heading compact">
        <span className="step-number">04</span>
        <div>
          <h2>Set the frame rate</h2>
          <p>Choose cinematic, standard, or high-motion playback.</p>
        </div>
      </div>
      <div className="frame-rate-row" role="radiogroup" aria-label="Frame rate">
        {VIDEO_FRAME_RATES.map((rate) => {
          const unavailable =
            rate < VIDEO_PLATFORM_SPECS[platform].minimumFrameRate;
          return (
            <button
              type="button"
              role="radio"
              aria-checked={frameRate === rate}
              disabled={unavailable}
              className={frameRate === rate ? "selected" : ""}
              key={rate}
              title={
                unavailable
                  ? `${VIDEO_PLATFORM_SPECS[platform].label} requires at least ${String(VIDEO_PLATFORM_SPECS[platform].minimumFrameRate)} fps.`
                  : undefined
              }
              onClick={() => setFrameRate(rate)}
            >
              <strong>{rate}</strong>
              <small>
                {rate === 24 ? "CINEMA" : rate === 30 ? "STANDARD" : "SMOOTH"}
              </small>
            </button>
          );
        })}
      </div>

      <div className="section-heading compact">
        <span className="step-number">05</span>
        <div>
          <h2>Set the length</h2>
          <p>Longer clips use more generation credit.</p>
        </div>
      </div>
      <div
        className="duration-row"
        role="radiogroup"
        aria-label="Video duration"
      >
        {VIDEO_DURATIONS.map((seconds) => (
          <button
            type="button"
            role="radio"
            aria-checked={durationSeconds === seconds}
            className={durationSeconds === seconds ? "selected" : ""}
            key={seconds}
            onClick={() => setDurationSeconds(seconds)}
          >
            {seconds}s
          </button>
        ))}
      </div>

      <button
        className="create-button"
        type="submit"
        disabled={disabled || story.trim().length < 12}
      >
        {disabled ? (
          <>
            <span className="loader" /> Queuing your film…
          </>
        ) : (
          <>
            {experience.submitLabel} <span>→</span>
          </>
        )}
      </button>
      <p className="cost-note">
        Powered by {profile.label}. Paid API credit is used for each render.
      </p>
    </form>
  );
}
